import { useCallback } from 'react';
import type React from 'react';

import { Button } from '@change/ds-core-ui';

import { Translate } from '@change-corgi/core-react/i18n';

import { usePetitionActionsPageState } from '#src/app/pages/actions/pageContext';
import { useIsCommentAfterVideoArm, useVideoFirstFlow } from '#src/app/pages/actions/shared/hooks';

import { useVideoContext, useVideosComponentContext } from '../../context';
import { useCamera } from '../../shared/hooks/useCamera';
import { useSkipRecording } from '../Welcome/components/SkipRecording/hooks/useSkipRecording';

/**
 * Way out of the recorder and the preview, for the arms whose flow continues to the comment. Without
 * it those screens have no exit but the page skip link, which abandons the flow entirely.
 */
export const ExitToCommentStep: React.FC = () => {
	const isCommentAfterVideoArm = useIsCommentAfterVideoArm();
	const { closeCamera } = useCamera();
	const {
		data: { isRecording },
		actions: { setBlob, setStream },
	} = useVideoContext();
	const {
		data: { isUploading },
		actions: { setErrorMessage, setUploadStep },
	} = useVideosComponentContext();
	const [, { setShareSupporterVoiceSuccess }] = usePetitionActionsPageState();
	const {
		data: { hasCommented },
	} = useVideoFirstFlow();
	const {
		actions: { handleSkip },
	} = useSkipRecording();

	const handleClick = useCallback(() => {
		closeCamera();
		setStream(null);
		setBlob(null);
		setErrorMessage(null);

		/*
		 * Reached the recorder from the post-comment screen's record CTA, so back out to that screen - the
		 * normal skip drops them on an empty comment form for a comment they already wrote. Untracked:
		 * they may already have fired the skip event at the ask, and counting it twice inflates the metric.
		 */
		if (hasCommented) {
			setUploadStep('ready');
			setShareSupporterVoiceSuccess(true);
			return;
		}

		handleSkip();
	}, [
		closeCamera,
		handleSkip,
		hasCommented,
		setBlob,
		setErrorMessage,
		setShareSupporterVoiceSuccess,
		setStream,
		setUploadStep,
	]);

	if (!isCommentAfterVideoArm) {
		return null;
	}

	return (
		<div className="text-center">
			<Button
				variant="tertiary"
				size="small"
				onClick={handleClick}
				/*
				 * The upload is not abortable, so tapping this mid-submit unmounts the flow while the video
				 * publishes anyway. Disabled while recording too, so exiting mid-take can't leave a blob in
				 * shared context with no matching duration (only `finishTake` sets that) and can't unmount the
				 * flow out from under a live `MediaRecorder`/camera stream. Disabled rather than hidden either
				 * way, so it does not vanish under a thumb.
				 */
				disabled={isUploading || isRecording}
				data-testid="exit-to-comment"
				data-qa="exit-to-comment"
			>
				<Translate value="corgi.petition_actions.skip" />
			</Button>
		</div>
	);
};
